import React, { useState, useCallback, useRef } from 'react';
import { 
  Box, 
  Typography, 
  LinearProgress, 
  IconButton,
  Paper,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import { 
  CloudUpload as CloudUploadIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import ApiFetch from '../ApiFetch';

// ファイルタイプの制限設定
const ALLOWED_EXTENSIONS = ['.ckpt', '.safetensors'];

// ファイルタイプの表示名
const FILE_TYPE_NAMES = {
  '.ckpt': 'Checkpoint File',
  '.safetensors': 'SafeTensors File'
};

const MAX_FILE_SIZE = 10 * 1024 * 1024 * 1024; // 10GB

// ドラッグ&ドロップエリアのスタイル
const UploadArea = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  border: `2px dashed ${theme.palette.divider}`,
  backgroundColor: theme.palette.grey[50],
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
}));

const FileUpload = () => {
  const [uploads, setUploads] = useState(new Map());
  const [isDragging, setIsDragging] = useState(false);
  const abortControllers = useRef(new Map());

  // ファイルの検証
  const validateFile = (file) => {
    if (file.size > MAX_FILE_SIZE) {
      return 'ファイルサイズは10GB以下にしてください';
    }
    
    const fileExtension = file.name.toLowerCase().match(/\.[0-9a-z]+$/);
    if (!fileExtension || !ALLOWED_EXTENSIONS.includes(fileExtension[0])) {
      return '対応していない拡張子です。(.ckpt または .safetensors のファイルを選択してください)';
    }
    
    return null;
  };

  // アップロード処理の開始
  const startUpload = async (file) => {
    const fileId = `${file.name}-${Date.now()}`;
    const controller = new AbortController();
    abortControllers.current.set(fileId, controller);

    setUploads(prev => new Map(prev).set(fileId, {
      file,
      progress: 0,
      status: 'uploading',
      error: null
    }));

    try {
      // 署名付きURLの取得
      const response = await fetch(ApiFetch.API_PROTOCOL+"//"+ApiFetch.API_HOST+'/v1/storage/signedUploadUrl', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: file.name,
          contentType: file.type
        }),
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error('署名付きURLの取得に失敗しました');
      }

      const json = await response.json();

      // ファイルアップロード
      const xhr = new XMLHttpRequest();
      xhr.open('POST', json.url);
      //xhr.setRequestHeader('Content-Type', "application/octet-stream");

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploads(prev => new Map(prev).set(fileId, {
            ...prev.get(fileId),
            progress
          }));
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          setUploads(prev => new Map(prev).set(fileId, {
            ...prev.get(fileId),
            status: 'completed',
            progress: 100
          }));
        } else {
          throw new Error('アップロードに失敗しました');
        }
      };

      xhr.onerror = () => {
        throw new Error('アップロードに失敗しました');
      };

      controller.signal.addEventListener('abort', () => {
        xhr.abort();
      });

      let fd = new FormData()
      fd.append("file", file)
      xhr.send(fd);

    } catch (err) {
      if (err.name === 'AbortError') {
        setUploads(prev => new Map(prev).set(fileId, {
          ...prev.get(fileId),
          status: 'cancelled',
          error: 'アップロードがキャンセルされました'
        }));
      } else {
        setUploads(prev => new Map(prev).set(fileId, {
          ...prev.get(fileId),
          status: 'error',
          error: err.message
        }));
      }
    } finally {
      abortControllers.current.delete(fileId);
    }
  };

  // ファイル選択ハンドラー
  const handleFileSelect = (selectedFiles) => {
    Array.from(selectedFiles).forEach(file => {
      const error = validateFile(file);
      if (error) {
        const fileId = `${file.name}-${Date.now()}`;
        setUploads(prev => new Map(prev).set(fileId, {
          file,
          progress: 0,
          status: 'error',
          error
        }));
      } else {
        startUpload(file);
      }
    });
  };

  // ドラッグ&ドロップハンドラー
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  }, []);

  // アップロードのキャンセル
  const handleCancel = (fileId) => {
    const controller = abortControllers.current.get(fileId);
    if (controller) {
      controller.abort();
    }
  };

  // アップロード済みファイルの削除
  const handleRemove = (fileId) => {
    setUploads(prev => {
      const next = new Map(prev);
      next.delete(fileId);
      return next;
    });
  };

  return (
    <Box sx={{ mx: 'auto', p: 2 }}>
      <Stack spacing={2}>
        {/* ドラッグ&ドロップエリア */}
        <UploadArea
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          elevation={isDragging ? 3 : 1}
          sx={{
            backgroundColor: isDragging ? 'grey.100' : 'grey.50',
          }}
        >
          <input
            type="file"
            onChange={(e) => handleFileSelect(e.target.files)}
            style={{ display: 'none' }}
            id="file-input"
            multiple
            accept={ALLOWED_EXTENSIONS.join(',')}
          />
          <label htmlFor="file-input">
            <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              ファイルをドラッグ&ドロップ
            </Typography>
            <Typography color="textSecondary" gutterBottom>
              または クリックしてファイルを選択
            </Typography>
            <Typography variant="body2" color="textSecondary">
              対応形式: {Object.values(FILE_TYPE_NAMES).join(', ')}
            </Typography>
          </label>
        </UploadArea>

        {/* アップロードリスト */}
        {uploads.size > 0 && (
          <List>
            {Array.from(uploads.entries()).map(([fileId, upload]) => (
              <ListItem
                key={fileId}
                sx={{
                  bgcolor: 'background.paper',
                  mb: 1,
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider'
                }}
              >
                <ListItemText
                  primary={upload.file.name}
                  secondary={
                    <Box>
                      {upload.status === 'uploading' && (
                        <LinearProgress 
                          variant="determinate" 
                          value={upload.progress}
                          sx={{ my: 1 }}
                        />
                      )}
                      <Typography variant="body2" color="textSecondary">
                        {upload.status === 'uploading' && `${upload.progress}%`}
                        {upload.status === 'completed' && '完了'}
                        {upload.status === 'error' && upload.error}
                        {upload.status === 'cancelled' && 'キャンセル済み'}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  {upload.status === 'uploading' ? (
                    <IconButton edge="end" onClick={() => handleCancel(fileId)}>
                      <CancelIcon />
                    </IconButton>
                  ) : (
                    <IconButton edge="end" onClick={() => handleRemove(fileId)}>
                      {upload.status === 'completed' ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <ErrorIcon color="error" />
                      )}
                    </IconButton>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Stack>
    </Box>
  );
};

export default FileUpload;